import React from 'react';
import { Link } from './Link';
import { JcCard } from './jc/JcCard';
import { EmployerScoreGauge } from './jc/EmployerScoreGauge';
import { mapEmployerScoreToTier } from '../utils/employer-score/mapScoreToTier';
import { adjustBrandColor } from '../utils/brand-color/adjust-brand-color';
import { pickTopStrengthKeys } from '../utils/best-companies/build-best-companies-listing-rating-explainer';
import type { AlgoliaCompanyRecord } from '../types/company-search-record';
import type { CompanyProfileUiCopy } from '../utils/company/company-profile-ui-copy';
import { Msg, useFormatMessage } from '../i18n-review/client/Msg';

interface SubScoreRowProps {
  label: string;
  score: number; // 0–1
  weight?: string;
  color: string;
}

function SubScoreRow({ label, score, weight, color }: SubScoreRowProps) {
  // No numeric percentage shown here (was `{pct}`) — same reasoning as the headline gauge:
  // a specific per-pillar number is exactly the kind of figure an employer could dispute.
  // The bar's fill still conveys relative strength; `weight` is the fixed methodology share
  // (same for every company), not a company-specific figure, so it's fine to keep as a number.
  const pct = Math.round(score * 100);
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <span className="w-[5.5rem] sm:w-28 shrink-0 truncate text-[13px] font-semibold text-jc-ink-secondary" title={label}>
        {label}
      </span>
      <div className="flex-1 min-w-0 h-2.5 rounded-full bg-jc-elevated">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      {weight ? <span className="w-9 shrink-0 text-right text-[11px] text-jc-muted">{weight}</span> : null}
    </div>
  );
}

/** Static formatMessage calls (not a dynamic id lookup) so the extraction tool can see them. */
function formatStrengthDimension(formatMessage: ReturnType<typeof useFormatMessage>, key: string): string | null {
  switch (key) {
    case 'pay':
      return formatMessage({ id: 'employerScoreCard.dimension.pay', defaultMessage: 'pay', description: "Lowercase noun naming the 'pay' dimension inside the sentence \"Strongest at {dimension}\"" });
    case 'flexibility':
      return formatMessage({ id: 'employerScoreCard.dimension.flexibility', defaultMessage: 'flexibility', description: "Lowercase noun naming the 'flexibility' dimension inside the sentence \"Strongest at {dimension}\"" });
    case 'stability':
      return formatMessage({ id: 'employerScoreCard.dimension.stability', defaultMessage: 'stability', description: "Lowercase noun naming the 'stability' dimension inside the sentence \"Strongest at {dimension}\"" });
    case 'growth':
      return formatMessage({ id: 'employerScoreCard.dimension.growth', defaultMessage: 'growth', description: "Lowercase noun naming the 'growth' dimension inside the sentence \"Strongest at {dimension}\"" });
    case 'benefits':
      return formatMessage({ id: 'employerScoreCard.dimension.benefits', defaultMessage: 'benefits', description: "Lowercase noun naming the 'benefits' dimension inside the sentence \"Strongest at {dimension}\"" });
    case 'transparency':
      return formatMessage({ id: 'employerScoreCard.dimension.transparency', defaultMessage: 'transparency', description: "Lowercase noun naming the 'transparency' dimension inside the sentence \"Strongest at {dimension}\"" });
    default:
      return null;
  }
}

interface EmployerScoreCardProps {
  company: Pick<
    AlgoliaCompanyRecord,
    | 'companyName'
    | 'brandColour'
    | 'employerScore'
    | 'payScore'
    | 'flexibilityScore'
    | 'stabilityScore'
    | 'growthScore'
    | 'benefitsScore'
    | 'transparencyScore'
  >;
  copy: Pick<CompanyProfileUiCopy, 'employerScorePendingTitle' | 'employerScorePendingDescription'>;
  className?: string;
}

export function EmployerScoreCard({ company, copy, className = '' }: EmployerScoreCardProps) {
  const formatMessage = useFormatMessage();

  if (company.employerScore === undefined) {
    return (
      <JcCard className={`p-4 sm:p-5 ${className}`}>
        <h2 className="font-display text-base font-bold text-jc-ink">
          <Msg id="employerScoreCard.title" defaultMessage="JobCrawls Employer Score" description="Employer score card heading" />
        </h2>
        <p className="mt-2 inline-flex items-center rounded-full bg-jc-elevated px-2.5 py-1 text-xs font-medium text-jc-on-elevated">
          {copy.employerScorePendingTitle}
        </p>
        <p className="mt-2 text-xs leading-relaxed text-jc-muted">{copy.employerScorePendingDescription}</p>
      </JcCard>
    );
  }

  const score = company.employerScore;
  const tier = mapEmployerScoreToTier(score);
  const { panel: brandColor } = adjustBrandColor(company.brandColour);

  /** Shares match `computeEmployerScore`'s COMPOSITE_WEIGHTS — all six pillars are wired in. */
  const mainSubScores = [
    { key: 'pay', label: formatMessage({ id: 'employerScoreCard.subscore.pay', defaultMessage: 'Pay', description: 'Employer score sub-dimension label' }), score: company.payScore ?? 0, weight: '25%' },
    { key: 'flexibility', label: formatMessage({ id: 'employerScoreCard.subscore.flexibility', defaultMessage: 'Flexibility', description: 'Employer score sub-dimension label' }), score: company.flexibilityScore ?? 0, weight: '20%' },
    { key: 'stability', label: formatMessage({ id: 'employerScoreCard.subscore.stability', defaultMessage: 'Stability', description: 'Employer score sub-dimension label' }), score: company.stabilityScore ?? 0, weight: '20%' },
    { key: 'growth', label: formatMessage({ id: 'employerScoreCard.subscore.growth', defaultMessage: 'Growth', description: 'Employer score sub-dimension label' }), score: company.growthScore ?? 0, weight: '15%' },
    { key: 'benefits', label: formatMessage({ id: 'employerScoreCard.subscore.benefits', defaultMessage: 'Benefits', description: 'Employer score sub-dimension label' }), score: company.benefitsScore ?? 0, weight: '10%' },
    { key: 'transparency', label: formatMessage({ id: 'employerScoreCard.subscore.transparency', defaultMessage: 'Transparency', description: 'Employer score sub-dimension label' }), score: company.transparencyScore ?? 0, weight: '10%' },
  ];

  const topStrengths = pickTopStrengthKeys(company, 0.52, 1);
  const topStrengthLabel = topStrengths[0] ? formatStrengthDimension(formatMessage, topStrengths[0]) : null;

  return (
    <JcCard className={`p-4 sm:p-5 ${className}`}>
      <div className="flex flex-col gap-4 min-[420px]:flex-row min-[420px]:items-center">
        <EmployerScoreGauge score={score} displayText={tier} size={92} strokeWidth={9} color={brandColor} trackColor="var(--jc-elevated)" className="mx-auto min-[420px]:mx-0 flex-none" />
        <div className="min-w-0 flex-1 text-center min-[420px]:text-left">
          <h2 className="font-display text-lg font-bold text-jc-ink">
            <Msg id="employerScoreCard.title" defaultMessage="JobCrawls Employer Score" description="Employer score card heading" />
          </h2>
          <p className="mt-0.5 text-xs font-medium text-jc-muted">
            <Msg
              id="employerScoreCard.disclaimer"
              defaultMessage="Computed from job data signals, not reviews"
              description="Clarifies the score is not a user-review-based rating, shown directly under the score card title"
            />
          </p>
          {topStrengthLabel ? (
            <p className="mt-2 inline-block rounded-full bg-jc-elevated px-3 py-1 font-display text-[13px] font-bold text-jc-ink">
              <Msg
                id="employerScoreCard.strongestAt"
                defaultMessage="Strongest at {dimension}"
                description="One-line summary naming this employer's highest-scoring dimension, e.g. 'Strongest at flexibility'"
                values={{ dimension: topStrengthLabel }}
              />
            </p>
          ) : null}
        </div>
      </div>

      <div className="mt-4 space-y-2.5 sm:mt-5 sm:space-y-3">
        {mainSubScores.map((row) => (
          <SubScoreRow key={row.key} label={row.label} score={row.score} weight={row.weight} color={brandColor} />
        ))}
      </div>

      <p className="mt-4 text-xs leading-relaxed text-jc-muted">
        <Msg
          id="employerScoreCard.methodology"
          defaultMessage="This grade is based on salary competitiveness from posted bands (where we have enough listings), remote and hybrid flexibility, hiring stability over time, growth signals from role mix and hiring volume trends, benefits and perks mentioned in postings, and transparency (how often postings disclose a real, non-estimated salary and a recruitment-process timeline)."
          description="Methodology paragraph explaining how the employer score is computed"
        />{' '}
        <Link
          href="/about-employer-score"
          className="font-medium text-jc-accent underline decoration-jc-accent/30 underline-offset-2 hover:text-jc-cta-hover"
        >
          <Msg id="employerScoreCard.howWeCalculate" defaultMessage="How we calculate this" description="Link text to the full employer score methodology page" />
        </Link>
      </p>
    </JcCard>
  );
}

EmployerScoreCard.displayName = 'EmployerScoreCard';
